import { useStore } from 'vuex';
import { Company } from '@/hooks/useCompanies';
import { ProductionType } from '@/hooks/useConstructor';
import { Debtor, DebtorOrganization } from '@/hooks/useDebtors';
import { ApiResponse, ListingRequestSource, ListingResponse } from '@/store/modules/api';

export enum RosreestrStatusModelType {
  data = 'data',
  rights = 'rights'
}

export type RosreestrStatusModel = {
  type: Array<RosreestrStatusModelType>;
  company_id: Company['id'];
  production_type: ProductionType;
  force: boolean;
  ignore_norights: boolean;
  debtor_ids?: Array<Debtor['pk']>;
  filters?: Record<any, any>;
}

export type RosreestrStatusResponse = {
  uuid: string;
}

export type RosreestrStatusLocalModel = Omit<RosreestrStatusModel, 'company_id' | 'type' | 'debtor_ids' | 'production_type'> & {
  data: boolean;
  rights: boolean;
  ignore_norights: boolean;
};

export type RosreestrEstateObjectOwner = {
  id: number;
  owner_name: string | null;
  birth_place: string | null;
  num_of_passport: string | null;
  passport_issued_by: string | null;
  inn: string | null;
  registered_ownership_type: string | null;
  fraction_in_ownership: string | null;
  ownership_registration_number: number | null;
  birth_date: Date | null;
  end_date: Date | null;
  date_of_passport_issue: Date | null;
  ownership_registration_date: Date | null;
}

export type RemoveCharacteristicModelResponse = {
  status: boolean;
}

export type RosreestrEstateObjectCharacteristic = {
  id: number;
  debtor_data: string;
  registered_ownership_type: string;
  owner_name: string;
  cadastral_number: string | null;
  fraction_in_ownership: string;
  ownership_type: string;
  ownership_name: string;
  ownership_registration_date: string | Date;
  ownership_registration_number: string;
  request_date: string | Date;
  last_request_date: string | Date;
  ownership_termination_info: string;
  debtor: Debtor['pk'];
  status_tracking: number;
  organization?: DebtorOrganization['debtor_main_profile']['organization'];
}

export type FetchRosreestrEstateObjectCharacteristicModel = ListingRequestSource<
  RosreestrEstateObjectCharacteristic
>

export type UpdateStandardizedDebtorAddressesResponse = any;

export type CreateCharacteristicModel = {
  debtor_id: Debtor['pk'];
  model: RosreestrEstateObjectCharacteristic;
}

export type CreateMovementModel = {
  debtor_id: Debtor['pk'];
  model: RosreestrEstateObjectMovement;
}

export type UpdateCharacteristicModel = {
  id: RosreestrEstateObjectCharacteristic['id'];
  model: RosreestrEstateObjectCharacteristic;
}

export type UpdateMovementModel = {
  id: RosreestrEstateObjectMovement['id'];
  model: RosreestrEstateObjectMovement;
}

export type RemoveCharacteristicModel = {
  id: RosreestrEstateObjectCharacteristic['id'];
}

export type RemoveMovementModel = {
  id: RosreestrEstateObjectMovement['id'];
}

export type FetchRosreestrEstateObjectCharacteristicResponse = ListingResponse<
  RosreestrEstateObjectCharacteristic
>

export type RosreestrEstateObjectMovement = {
  id: number;
  debtor_data: string;
  registered_ownership_type: string;
  owner_name: string;
  cadastral_number: string;
  fraction_in_ownership: string;
  ownership_type: string;
  ownership_name: string;
  ownership_registration_date: string | Date;
  ownership_registration_number: string;
  end_date: string;
  debtor: Debtor['pk'];
  status_tracking: number;
  date_from?: Date | null;
  date_to?: Date | null;
}

export type RosreestrEstateObjectRecord = any

export type FetchRosreestrEstateObjectMovementModel = ListingRequestSource<
  RosreestrEstateObjectMovement
>

export type FetchRosreestrEstateObjectMovementResponse = ListingResponse<
  RosreestrEstateObjectMovement
>

export type FetchRosreestrEstateObjectMovementGrouppedResponse = {
  date_from: string | null;
  date_to: string | null;
} & RosreestrEstateObjectMovement;

export type StandardizedDebtorAddress = {
  id: number;
  cadnum: string;
  address: string;

  area: string;
  object_name: string;
  land_category: string;
  cad_cost: string;
}

export type FetchStandardizedDebtorAddressesModel = {
  id: Debtor['debtor_main_profile']['id'];
}

export type FetchStandardizedDebtorAddressesResponse = {
  cadnum: string|null;
  cad_num: string|null;
  created_at: string|null;
  updated_at: string|null;
  suggests: Array<StandardizedDebtorAddress> | null;
}

export type FetchEstateRightInfoResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<{
    id: number;
    created_at: string;
    updated_at: string;
    cadnum: string;
    number: string;
    type: string;
    date: string;
  }>;
}

export type FetchEstateEncumbranceInfoResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: Array<{
    id: number;
    created_at: string;
    updated_at: string;
    cadnum: string;
    number: string;
    type: string;
    date: string;
  }>;
}

export type UpdateStandardizedDebtorAddressesModel = {
  id: Debtor['debtor_main_profile']['id'];
  cad_num: string;
}

export type fetchEstateRightInfo = {
  company_id: number;
  cadnum: any;
}

export type fetchEstateEncumbranceInfo = {
  company_id: number;
  cadnum: any;
}

export type UpdateCharacteristicModelResponse = any;
export type UpdateMovementModelResponse = any;

export const useRosreestr = () => {
  const store = useStore();

  const extractFromEgrn = async (
    payload: RosreestrStatusModel,
  ): Promise<ApiResponse<RosreestrStatusResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/extractFromEgrn', payload);

    return { status, response };
  };

  const fetchEstateObjectCharacteristics = async (
    payload: FetchRosreestrEstateObjectCharacteristicModel,
  ): Promise<ApiResponse<FetchRosreestrEstateObjectCharacteristicResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/fetchEstateObjectCharacteristics', payload);

    return { status, response };
  };

  const createEstateObjectCharacteristic = async (
    payload: CreateCharacteristicModel,
  ): Promise<ApiResponse<UpdateCharacteristicModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/createEstateObjectCharacteristic', payload);

    return { status, response };
  };

  const updateEstateObjectCharacteristic = async (
    payload: UpdateCharacteristicModel,
  ): Promise<ApiResponse<UpdateCharacteristicModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/updateEstateObjectCharacteristic', payload);

    return { status, response };
  };

  const removeEstateObjectCharacteristic = async (
    payload: RemoveCharacteristicModel,
  ): Promise<ApiResponse<RemoveCharacteristicModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/removeEstateObjectCharacteristic', payload);

    return { status, response };
  };

  const createEstateObjectMovement = async (
    payload: CreateMovementModel,
  ): Promise<ApiResponse<UpdateMovementModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/createEstateObjectMovement', payload);

    return { status, response };
  };

  const updateEstateObjectMovement = async (
    payload: UpdateMovementModel,
  ): Promise<ApiResponse<UpdateMovementModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/updateEstateObjectMovement', payload);

    return { status, response };
  };

  const removeEstateObjectMovement = async (
    payload: RemoveMovementModel,
  ): Promise<ApiResponse<RemoveCharacteristicModelResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/removeEstateObjectMovement', payload);

    return { status, response };
  };

  const fetchEstateObjectMovements = async (
    payload: FetchRosreestrEstateObjectMovementModel,
  ): Promise<ApiResponse<FetchRosreestrEstateObjectMovementResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/fetchEstateObjectMovements', payload);

    return { status, response };
  };

  const fetchStandardizedDebtorAddresses = async (
    payload: FetchStandardizedDebtorAddressesModel,
  ): Promise<ApiResponse<FetchStandardizedDebtorAddressesResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/fetchStandardizedDebtorAddresses', payload);

    return { status, response };
  };

  const updateStandardizedDebtorAddress = async (
    payload: UpdateStandardizedDebtorAddressesModel,
  ): Promise<ApiResponse<UpdateStandardizedDebtorAddressesResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/updateStandardizedDebtorAddress', payload);

    return { status, response };
  };

  const fetchEstatesRightInfo = async (
    payload: fetchEstateRightInfo,
  ): Promise<ApiResponse<FetchEstateRightInfoResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/fetchEstatesRightInfo', payload);

    return { status, response };
  };

  const fetchEstatesEncumbranceInfo = async (
    payload: fetchEstateEncumbranceInfo,
  ): Promise<ApiResponse<FetchEstateEncumbranceInfoResponse>> => {
    const { status, response } = await store.dispatch('rosreestr/fetchEstatesEncumbranceInfo', payload);

    return { status, response };
  };

  return {
    extractFromEgrn,
    fetchEstateObjectCharacteristics,
    fetchEstateObjectMovements,
    fetchStandardizedDebtorAddresses,
    updateStandardizedDebtorAddress,
    fetchEstatesRightInfo,
    fetchEstatesEncumbranceInfo,

    createEstateObjectCharacteristic,
    updateEstateObjectCharacteristic,
    removeEstateObjectCharacteristic,

    createEstateObjectMovement,
    updateEstateObjectMovement,
    removeEstateObjectMovement,
  };
};
